
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";

const Index = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        
        // Only apply theme from localStorage if it's explicitly set
        const storedTheme = localStorage.getItem('theme');
        
        if (storedTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else if (storedTheme === 'light') {
          document.documentElement.classList.remove('dark');
        } else if (storedTheme === 'system') {
          // System preference
          const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          document.documentElement.classList.toggle('dark', systemPrefersDark);
        }
        
        // Check for a current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log("User is logged in:", session.user.id);
          
          // Check if user exists in users table, if not create them
          const { data: existingUser, error: fetchError } = await supabase
            .from('users')
            .select('id, name, email')
            .eq('id', session.user.id)
            .maybeSingle();
            
          if (fetchError) {
            console.error("Error checking if user exists:", fetchError);
            toast.error("Erro ao verificar dados do usuário");
          } else if (!existingUser) {
            console.log("User not found in users table, creating...");
            // Create new user entry
            const { error: insertError } = await supabase
              .from('users')
              .insert({
                id: session.user.id,
                email: session.user.email,
                name: session.user.email?.split('@')[0] || 'Usuário',
                user_type: 'company'
              });
              
            if (insertError) {
              console.error("Error creating user entry:", insertError);
              toast.error("Erro ao criar perfil de usuário");
            } else {
              toast.success("Perfil de usuário criado com sucesso!");
            }
          } else {
            console.log("User found in database:", existingUser);
          }
          
          // User is logged in, try to get theme preference from database
          try {
            const { data } = await supabase
              .from('users')
              .select('theme')
              .eq('id', session.user.id)
              .maybeSingle();
            
            if (data?.theme && !storedTheme) {
              // Only store in localStorage if there's no existing preference
              localStorage.setItem('theme', data.theme);
              
              // Only apply the theme if it's explicit
              const userTheme = data.theme as 'light' | 'dark' | 'system';
              if (userTheme === 'dark') {
                document.documentElement.classList.add('dark');
              } else if (userTheme === 'light') {
                document.documentElement.classList.remove('dark');
              } else if (userTheme === 'system') {
                const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                document.documentElement.classList.toggle('dark', systemPrefersDark);
              }
            }
          } catch (dbError) {
            console.error("Error fetching theme from database:", dbError);
            // Don't block navigation if theme fetch fails
          }
          
          // User is logged in, navigate to dashboard page
          localStorage.setItem("isAuthenticated", "true");
          navigate("/dashboard");
        } else {
          // User is not logged in, navigate to login page
          localStorage.removeItem("isAuthenticated");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        toast.error("Erro ao verificar autenticação");
        localStorage.removeItem("isAuthenticated");
        navigate("/login");
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth event:", event);
        
        if (event === 'SIGNED_IN' && session) {
          // Don't show toast here - handled in App.tsx
          navigate('/dashboard');
        } else if (event === 'SIGNED_OUT') {
          navigate('/login');
        }
      }
    );

    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Show loading while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground transition-colors duration-300">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-primary mb-4">Minha Agência</h1>
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    </div>
  );
};

export default Index;
